import { useEffect, useRef } from 'react';
import { useWebRTC } from '../../hooks/useWebRTC';
import { useAuth } from '../../contexts/AuthContext';
import { useCallAudio } from '../../hooks/useCallAudio';

/**
 * This component handles incoming calls for the authenticated owner.
 * It should be mounted in the authenticated app layout.
 */
export default function IncomingCallHandler() {
  const { user, token } = useAuth();
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const { playRingtone, stopRingtone } = useCallAudio();

  const {
    callState,
    error,
    incomingCall,
    localStream,
    remoteStream,
    isAudioEnabled,
    isVideoEnabled,
    isScreenSharing,
    acceptCall,
    rejectCall,
    endCall,
    toggleAudio,
    toggleVideo,
    toggleScreenShare,
  } = useWebRTC(token || undefined);

  // Attach streams to video elements
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream]);

  // Play ringtone on incoming call
  useEffect(() => {
    if (callState === 'incoming') {
      // Play ringtone audio
      playRingtone();

      // Show browser notification
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Incoming Call', {
          body: `${incomingCall?.callerName || 'Someone'} is calling...`,
          icon: '/pwa-192x192.png',
          tag: 'incoming-call',
          requireInteraction: true,
        });
      }
    } else {
      // Stop ringtone when call state changes from incoming
      stopRingtone();
    }

    // Cleanup on unmount
    return () => {
      stopRingtone();
    };
  }, [callState, incomingCall, playRingtone, stopRingtone]);

  // Only render if the user is the owner (w@wezireland.com)
  if (user?.email !== 'w@wezireland.com') {
    return null;
  }

  // No call activity
  if (callState === 'idle' && !incomingCall) {
    return null;
  }

  const isInCall = callState === 'connecting' || callState === 'connected';

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center">
      {/* Incoming call notification */}
      {callState === 'incoming' && incomingCall && (
        <div className="text-center p-8">
          <div className="animate-pulse mb-6">
            <div className="w-32 h-32 mx-auto rounded-full bg-primary-600 flex items-center justify-center">
              <PhoneRingingIcon className="w-16 h-16 text-white" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Incoming Call</h2>
          <p className="text-lg text-neutral-300 mb-8">
            {incomingCall.callerName || 'Unknown caller'}
          </p>
          <div className="flex justify-center gap-6">
            <button
              onClick={rejectCall}
              className="p-5 rounded-full bg-red-600 hover:bg-red-700 text-white shadow-lg"
              aria-label="Reject call"
            >
              <PhoneOffIcon className="w-8 h-8" />
            </button>
            <button
              onClick={acceptCall}
              className="p-5 rounded-full bg-green-600 hover:bg-green-700 text-white shadow-lg"
              aria-label="Accept call"
            >
              <PhoneIcon className="w-8 h-8" />
            </button>
          </div>
        </div>
      )}

      {/* Active call */}
      {isInCall && (
        <div className="w-full h-full flex flex-col">
          {/* Video area */}
          <div className="flex-1 relative">
            {/* Remote video */}
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              className="w-full h-full object-cover"
            />

            {/* Audio-only indicator */}
            {!remoteStream?.getVideoTracks().length && (
              <div className="absolute inset-0 flex items-center justify-center bg-neutral-900">
                <div className="text-center">
                  <div className="w-32 h-32 mx-auto rounded-full bg-neutral-800 flex items-center justify-center mb-4">
                    <span className="text-5xl">
                      {incomingCall?.callerName?.[0] || '?'}
                    </span>
                  </div>
                  <p className="text-xl text-white">
                    {incomingCall?.callerName || 'Caller'}
                  </p>
                  <p className="text-neutral-400 mt-2">
                    {callState === 'connecting' ? 'Connecting...' : 'Voice call'}
                  </p>
                </div>
              </div>
            )}

            {/* Local video PiP */}
            {(isVideoEnabled || isScreenSharing) && localStream && (
              <div className="absolute bottom-24 right-6 w-48 aspect-video bg-neutral-900 rounded-xl overflow-hidden shadow-2xl">
                <video
                  ref={localVideoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Error message */}
            {error && (
              <div className="absolute top-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-red-600 text-white rounded-lg">
                {error}
              </div>
            )}
          </div>

          {/* Call controls */}
          <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
            <div className="flex justify-center gap-4">
              <button
                onClick={toggleAudio}
                className={`p-4 rounded-full transition-colors ${
                  isAudioEnabled
                    ? 'bg-neutral-700 hover:bg-neutral-600'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
                aria-label={isAudioEnabled ? 'Mute' : 'Unmute'}
              >
                {isAudioEnabled ? (
                  <MicIcon className="w-6 h-6 text-white" />
                ) : (
                  <MicOffIcon className="w-6 h-6 text-white" />
                )}
              </button>

              <button
                onClick={toggleVideo}
                className={`p-4 rounded-full transition-colors ${
                  isVideoEnabled
                    ? 'bg-neutral-700 hover:bg-neutral-600'
                    : 'bg-neutral-700 hover:bg-neutral-600'
                }`}
                aria-label={isVideoEnabled ? 'Stop video' : 'Start video'}
              >
                {isVideoEnabled ? (
                  <VideoIcon className="w-6 h-6 text-white" />
                ) : (
                  <VideoOffIcon className="w-6 h-6 text-white" />
                )}
              </button>

              <button
                onClick={toggleScreenShare}
                className={`p-4 rounded-full transition-colors ${
                  isScreenSharing
                    ? 'bg-primary-600 hover:bg-primary-700'
                    : 'bg-neutral-700 hover:bg-neutral-600'
                }`}
                aria-label={isScreenSharing ? 'Stop sharing' : 'Share screen'}
              >
                <ScreenShareIcon className="w-6 h-6 text-white" />
              </button>

              <button
                onClick={endCall}
                className="p-4 rounded-full bg-red-600 hover:bg-red-700"
                aria-label="End call"
              >
                <PhoneOffIcon className="w-6 h-6 text-white" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Call ended */}
      {callState === 'ended' && (
        <div className="text-center p-8">
          <div className="w-24 h-24 mx-auto rounded-full bg-neutral-800 flex items-center justify-center mb-6">
            <PhoneOffIcon className="w-12 h-12 text-neutral-400" />
          </div>
          <h2 className="text-xl text-white mb-4">Call ended</h2>
        </div>
      )}
    </div>
  );
}

// Icons
function PhoneIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

function PhoneOffIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16 3h5m0 0v5m0-5l-6 6M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.517l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.279 3H5z" />
    </svg>
  );
}

function PhoneRingingIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 3v4m0-4l2 2m-2-2l-2 2m6 1v4m0-4l2 2m-2-2l-2 2" />
    </svg>
  );
}

function MicIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
    </svg>
  );
}

function MicOffIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
    </svg>
  );
}

function VideoIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
    </svg>
  );
}

function VideoOffIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18" />
    </svg>
  );
}

function ScreenShareIcon({ className = "w-5 h-5" }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}
